import {
  AlertBar,
  Table,
  spacing3,
  spacing6,
} from '@leaftrade/leaftrade-component-library';
import React, { useMemo, useRef } from 'react';
import QuickOrderQuantityCellContext from './QuickOrderQuantityCellContext';
import useQuickOrderTableHeight from '../../hooks/QuickOrder/useQuickOrderTableHeight';
import useQuickOrderTableModel from '../../hooks/QuickOrder/useQuickOrderTableModel';
import { useStorefrontState } from '../../../hooks/useReduxState';
import { QuickOrderTableNavigationContext } from './QuickOrderTableNavigationContext';
import { QuickOrderTableWrapper } from './QuickOrderTable.styles';
import QuickOrderTableFooterActions from './QuickOrderTableFooterActions';
import QuickOrderTableHeaderActions from './QuickOrderTableHeaderActions';

const QUICK_ORDER_ROW_HEIGHT = 113;

const getQuickOrderRowId = (item) => String(item.cartItemId);

const QuickOrderTable = () => {
  const { quickOrderData, vendor } = useStorefrontState();
  const { quickOrderAlerts } = quickOrderData;
  const quantityCellStateRef = useRef({
    activeInputKey: null,
    drafts: {},
  });
  const maxTableHeight = useQuickOrderTableHeight();
  const { tableColumns, tableData, totals } = useQuickOrderTableModel({
    quickOrderData,
    vendor,
  });

  const virtualizedRowScrollRef = useRef(null);
  const navigationContextValue = useMemo(
    () => ({
      orderedRowIds: tableData.map((row) => String(row.cartItemId)),
      virtualizedRowScrollRef,
    }),
    [tableData]
  );

  const preHeader = quickOrderAlerts?.length ? (
    <>
      {quickOrderAlerts.map((alert) => (
        <AlertBar
          animationIn={null}
          hideClose
          alertMessages={[
            alert.subtitle || alert.sub_message || '',
            alert.level === 'error'
              ? 'Please review and update below.'
              : 'Please review below.',
          ]}
          alertTitle={alert.title || alert.message || ''}
          alertSeverity={alert.level}
          sx={{
            marginBottom: spacing3,
          }}
          testingId='quick-order--cart-alerts--alert-bar'
          key={alert.id}
        />
      ))}
    </>
  ) : null;

  return (
    <QuickOrderTableNavigationContext.Provider value={navigationContextValue}>
      <QuickOrderQuantityCellContext.Provider value={quantityCellStateRef}>
        <QuickOrderTableWrapper data-testid='quick-order-table--component--div'>
          <Table
            columnVisibilityLocalStorageKey='quick-order-table'
            emptyTable='No items in Quick Order.'
            enableColumnDragging={false}
            enableColumnResizing={false}
            footerActions={
              tableData.length ? (
                <QuickOrderTableFooterActions
                  totalItems={totals.totalItems}
                  totalPrice={totals.totalPrice}
                />
              ) : null
            }
            headerActions={<QuickOrderTableHeaderActions />}
            hideColumnToggles
            hideResetColumnOrdering
            hideResetColumnWidths
            idGetter={getQuickOrderRowId}
            localStorageKeyPrefix='quick-order'
            maxHeaderLines={2}
            maxTableHeight={maxTableHeight}
            preHeader={preHeader}
            rowHeight={QUICK_ORDER_ROW_HEIGHT}
            showFullScreen={false}
            showPagination={false}
            sx={{
              marginTop: spacing6,
              width: '100%',
            }}
            tableColumns={tableColumns}
            tableData={tableData}
            testingId='quick-order-table--table'
            virtualizeRows
            virtualizedRowScrollRef={virtualizedRowScrollRef}
          />
        </QuickOrderTableWrapper>
      </QuickOrderQuantityCellContext.Provider>
    </QuickOrderTableNavigationContext.Provider>
  );
};

export default QuickOrderTable;
